/**
 * Single source of truth for order hotel name resolution (LDS / packages / hotel rows).
 * Trace: [ORDER_HOTEL_NAME_RESOLVE_TRACE]
 */

import { decodeLdsDealPayload } from "./decodeLdsDealPayload.js";
import { safeOrderResponseObject } from "./packageOrderCardSummary.js";
import { isFlightsOnlyOrder } from "./resolveOrderProductDisplay.js";

export const HOTEL_NAME_UNAVAILABLE = "שם מלון לא זמין";

export const BANNED_HOTEL_NAME_LITERALS = new Set([
  "חבילת נופש",
  "LDS Package",
  "Package",
  "FlightsHotel",
  "Vacation Package",
]);

const TRACE_TAG = "[ORDER_HOTEL_NAME_RESOLVE_TRACE]";

function pickTrimmedString(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    if (typeof c === "number" && Number.isFinite(c)) {
      const s = String(c).trim();
      if (s) return s;
      continue;
    }
    if (typeof c === "string") {
      const t = c.trim();
      if (t) return t;
    }
  }
  return "";
}

function normBanKey(s) {
  return String(s || "").trim().toLowerCase();
}

/** @param {unknown} value */
export function isBannedHotelName(value) {
  const t = pickTrimmedString(value);
  if (!t) return true;
  if (BANNED_HOTEL_NAME_LITERALS.has(t)) return true;
  const low = normBanKey(t);
  for (const banned of BANNED_HOTEL_NAME_LITERALS) {
    if (normBanKey(banned) === low) return true;
  }
  return false;
}

/** @param {unknown} value */
export function isUsableHotelName(value) {
  const t = pickTrimmedString(value);
  return t.length >= 2 && !isBannedHotelName(t);
}

/** @param {object|null|undefined} h */
function pickHotelNameFromHotelDict(h) {
  if (!h || typeof h !== "object" || Array.isArray(h)) return "";
  return pickTrimmedString(
    h.hotelName,
    h.HotelName,
    h.hotelname,
    h.hotel_name,
    h.name,
    h.title,
    h.fullname,
    h.fullName,
    h.HOTEL_NAME
  );
}

/** @param {unknown} val */
function maybeJsonObject(val) {
  if (val == null) return null;
  if (typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    const t = val.trim();
    if (t.length < 2) return null;
    try {
      const p = JSON.parse(t);
      return p && typeof p === "object" && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Parse JSON dict from plain JSON or Base64-encoded JSON (dealid / offerNumber style).
 * @param {unknown} blob
 * @returns {object|null}
 */
export function decodeJsonOrBase64Dict(blob) {
  if (blob == null) return null;
  if (typeof blob === "object" && !Array.isArray(blob)) return blob;
  if (typeof blob !== "string") return null;
  const s = blob.trim();
  if (!s) return null;

  const tryJson = (text) => {
    try {
      const o = JSON.parse(text);
      return o && typeof o === "object" && !Array.isArray(o) ? o : null;
    } catch {
      return null;
    }
  };

  const direct = tryJson(s);
  if (direct) return direct;

  const padded = s + "=".repeat((4 - (s.length % 4)) % 4);
  for (const raw of [s, padded]) {
    try {
      const bin =
        typeof atob === "function"
          ? atob(raw)
          : Buffer.from(raw, "base64").toString("binary");
      const txt = decodeURIComponent(
        Array.from(bin, (c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join("")
      );
      const d = tryJson(txt);
      if (d) return d;
    } catch {
      /* try next */
    }
    try {
      const txt =
        typeof Buffer !== "undefined"
          ? Buffer.from(raw, "base64").toString("utf-8")
          : null;
      if (txt) {
        const d = tryJson(txt);
        if (d) return d;
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

/**
 * Decode ourprice.customerQuoteDisplay.offerNumber (object | JSON | Base64 JSON).
 * @param {object|null|undefined} row
 * @returns {{ payload: object, source: string } | null}
 */
export function resolveCustomerQuoteOfferNumberBlock(row) {
  const op = ourpriceBlock(row || {});
  if (!op) return null;
  const cq =
    op.customerQuoteDisplay ??
    op.customer_quote_display ??
    op.CustomerQuoteDisplay ??
    null;
  if (!cq || typeof cq !== "object") return null;
  const onRaw = cq.offerNumber ?? cq.offer_number ?? cq.OfferNumber ?? null;
  if (onRaw == null) return null;
  if (typeof onRaw === "object" && !Array.isArray(onRaw)) {
    return { payload: onRaw, source: "ourprice.customerQuoteDisplay.offerNumber" };
  }
  const decoded = decodeJsonOrBase64Dict(onRaw);
  if (!decoded) return null;
  return { payload: decoded, source: "ourprice.customerQuoteDisplay.offerNumber" };
}

/** @param {object|null|undefined} payload */
export function extractQuoteOfferDisplayFields(payload) {
  if (!nonEmptyObject(payload)) {
    return {
      hotelName: "",
      hotelBoard: "",
      roomType: "",
      hotelNights: null,
      hotelCity: "",
    };
  }
  const hotelName = pickTrimmedString(
    payload.hotelName,
    payload.hotel_name,
    payload.HotelName,
    payload.name
  );
  const hotelBoard = pickTrimmedString(
    payload.hotelBoard,
    payload.hotel_board,
    payload.board,
    payload.boardBasis,
    payload.board_basis
  );
  const roomType = pickTrimmedString(
    payload.roomType,
    payload.room_type,
    payload.ROOM_TYPE,
    payload.roomName,
    payload.room_name
  );
  const hotelCity = pickTrimmedString(
    payload.hotelCity,
    payload.hotel_city,
    payload.city,
    payload.destination,
    payload.destCity
  );
  let hotelNights = null;
  const nightsRaw = payload.hotelNights ?? payload.hotel_nights ?? payload.nights;
  if (typeof nightsRaw === "number" && Number.isFinite(nightsRaw) && nightsRaw > 0) {
    hotelNights = nightsRaw;
  } else if (nightsRaw != null && String(nightsRaw).trim()) {
    const n = Number(nightsRaw);
    if (Number.isFinite(n) && n > 0) hotelNights = n;
  }
  return { hotelName, hotelBoard, roomType, hotelNights, hotelCity };
}

function nonEmptyObject(o) {
  return o != null && typeof o === "object" && !Array.isArray(o) && Object.keys(o).length > 0;
}

/** @param {object[]} out @param {string} source @param {unknown} value */
function pushCandidate(out, source, value) {
  const t = pickTrimmedString(value);
  if (!t) return;
  out.push({ source, value: t, banned: isBannedHotelName(t) });
}

/** @param {object[]} out @param {string} source @param {object|null|undefined} h */
function pushHotelDictCandidate(out, source, h) {
  if (!nonEmptyObject(h)) return;
  pushCandidate(out, source, pickHotelNameFromHotelDict(h));
}

function collectDealSnapshotHotels(row, or) {
  const hotels = [];
  const snaps = [row.deal_snapshot, row.dealSnapshot, or?.deal_snapshot, or?.dealSnapshot];
  for (const s of snaps) {
    const h = s?.hotel;
    if (nonEmptyObject(h)) hotels.push(h);
  }
  const extBlocks = [or?.extended, row.extended].filter(Boolean);
  for (const ext of extBlocks) {
    if (Array.isArray(ext)) {
      for (const block of ext) {
        const h = block?.hotel;
        if (nonEmptyObject(h)) hotels.push(h);
      }
    } else if (nonEmptyObject(ext?.hotel)) {
      hotels.push(ext.hotel);
    }
  }
  return hotels;
}

function collectLdsDealDecodeCandidates(row, or) {
  const c = [];
  const push = (v) => {
    if (v == null || v === "") return;
    if (typeof v === "string" && !v.trim()) return;
    c.push(v);
  };

  if (typeof or === "string" && or.trim().length > 2) push(or);
  if (typeof row.order_response === "string" && row.order_response.trim().length > 2) {
    push(row.order_response);
  }

  push(row.ldsDealId, row.lds_deal_id, or?.ldsDealId, or?.lds_deal_id);
  push(row.deal_snapshot?.ldsDealId, row.deal_snapshot?.lds_deal_id);
  push(row.dealSnapshot?.ldsDealId, row.dealSnapshot?.lds_deal_id);

  const lf = pickLinkedFlightsRoot(row, or);
  if (Array.isArray(lf)) {
    for (const item of lf) push(item?.ldsDealId ?? item?.lds_deal_id);
  } else if (lf && typeof lf === "object") {
    push(lf.ldsDealId ?? lf.lds_deal_id);
  }

  passengersInfo(row).slice(0, 5).forEach((slot) => {
    const rm = slot?.room;
    if (nonEmptyObject(rm)) {
      push(rm.ldsDealId, rm.lds_deal_id);
    }
  });

  const extSources = [or?.extended, row.extended];
  for (const ext of extSources) {
    if (!Array.isArray(ext)) continue;
    for (const block of ext) {
      if (block == null) continue;
      if (typeof block === "string") {
        push(block);
        continue;
      }
      if (typeof block === "object") {
        push(block.ldsDealId, block.lds_deal_id);
        push(block.deal?.ldsDealId, block.deal?.lds_deal_id);
        push(block.encodedDeal, block.encoded_deal);
      }
    }
  }

  push(or?.dealToken, or?.encodedDeal, row.encodedDeal, row.dealToken);
  push(row.dealId, row.dealid);

  return c;
}

function tryDecodeHotelNameFromRow(row, or) {
  const candidates = collectLdsDealDecodeCandidates(row, or);
  for (let i = 0; i < candidates.length; i++) {
    const d = decodeLdsDealPayload(candidates[i]);
    if (!d?.hotelName) continue;
    return { value: d.hotelName, source: `dealid_decoded[${i}]` };
  }
  return null;
}

function ourpriceBlock(row) {
  return maybeJsonObject(row.ourprice) ?? maybeJsonObject(row.ourPrice) ?? null;
}

function extendedBlocks(row, or) {
  const out = [];
  for (const ext of [or?.extended, row.extended]) {
    if (Array.isArray(ext)) {
      for (const block of ext) {
        if (nonEmptyObject(block)) out.push(block);
      }
    } else if (nonEmptyObject(ext)) {
      out.push(ext);
    }
  }
  return out;
}

/**
 * extended.extendedDeal[].hrDealSnapshot — HR combo snapshot (Base64 JSON) after ldsDealTokenSsot.
 * @param {object} row
 * @param {object|null} or
 * @returns {Array<{ index: number, snap: unknown }>}
 */
function collectExtendedDealHrSnapshotEntries(row, or) {
  const out = [];
  const seen = new Set();
  for (const extRoot of [row.extended, or?.extended]) {
    if (extRoot == null) continue;
    const roots = Array.isArray(extRoot) ? extRoot : [extRoot];
    for (const root of roots) {
      if (!nonEmptyObject(root)) continue;
      const dealRows =
        root.extendedDeal ?? root.extended_deal ?? root.ExtendedDeal ?? root.ExtendedDeals;
      if (!Array.isArray(dealRows)) continue;
      for (let i = 0; i < dealRows.length && i < 5; i++) {
        const dealRow = dealRows[i];
        if (!nonEmptyObject(dealRow)) continue;
        const snap = dealRow.hrDealSnapshot ?? dealRow.hr_deal_snapshot ?? dealRow.HrDealSnapshot;
        if (snap == null || snap === "") continue;
        const key = `${i}:${typeof snap === "string" ? snap.slice(0, 48) : String(snap)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ index: i, snap });
      }
    }
  }
  return out;
}

/**
 * Decode hrDealSnapshot blobs and push hotelName into the SSOT candidate chain.
 * @returns {ReturnType<typeof extractQuoteOfferDisplayFields> | null}
 */
function pushHrDealSnapshotCandidates(rawCandidates, row, or) {
  let snapshotFields = null;
  for (const { index, snap } of collectExtendedDealHrSnapshotEntries(row, or)) {
    const payload = decodeJsonOrBase64Dict(snap);
    if (!nonEmptyObject(payload)) continue;
    const source = `extended.extendedDeal[${index}].hrDealSnapshot`;
    pushCandidate(rawCandidates, source, payload.hotelName ?? payload.hotel_name);
    if (!snapshotFields) {
      const fields = extractQuoteOfferDisplayFields(payload);
      if (fields.hotelName || fields.hotelCity || fields.hotelBoard) {
        snapshotFields = fields;
      }
    }
  }
  return snapshotFields;
}

function passengersInfo(row) {
  const p = row.passengersinfo ?? row.passengers_info ?? row.passengersInfo;
  return Array.isArray(p) ? p : [];
}

function pickLinkedFlightsRoot(row, or) {
  return (
    row?.linkedflights ??
    row?.linkedFlights ??
    row?.linked_flights ??
    or?.linkedflights ??
    or?.linkedFlights ??
    or?.linked_flights ??
    null
  );
}

function linkedFlightsBlock(row, or) {
  const lf = pickLinkedFlightsRoot(row, or);
  if (Array.isArray(lf)) return lf;
  if (nonEmptyObject(lf)) return [lf];
  return [];
}

function supplementBlock(row) {
  const sup = row.supplement ?? row.supplements;
  if (Array.isArray(sup)) return sup;
  if (nonEmptyObject(sup)) return [sup];
  return [];
}

function resolveBookingRef(row) {
  return pickTrimmedString(
    row.booking_ref,
    row.bookingRef,
    row.hr_order_number,
    row.hrOrderNumber
  );
}

function resolveOrderId(row) {
  return pickTrimmedString(row.id, row.dealid, row.dealId, row.orderid, row.orderId);
}

function emitHotelNameTrace(payload) {
  if (typeof import.meta !== "undefined" && import.meta.env && !import.meta.env.DEV) return;
  if (typeof process !== "undefined" && process.env?.ORDER_HOTEL_NAME_SSOT_TRACE === "0") return;
  // eslint-disable-next-line no-console
  console.log(TRACE_TAG, payload);
}

/** @param {ReturnType<typeof extractQuoteOfferDisplayFields> | null | undefined} fields */
function hasQuoteOfferDisplayFields(fields) {
  if (!fields || typeof fields !== "object") return false;
  return Boolean(
    pickTrimmedString(fields.hotelName, fields.hotelCity, fields.hotelBoard, fields.roomType) ||
      (typeof fields.hotelNights === "number" && fields.hotelNights > 0)
  );
}

/**
 * Resolve hotel name from an order row using the canonical priority chain.
 * @param {object|null|undefined} orderRow
 * @param {{ emitTrace?: boolean }} [options]
 * @returns {{ hotelName: string, source: string, rawCandidates: Array<{ source: string, value: string, banned: boolean }>, resolved: boolean }}
 */
export function resolveOrderHotelNameSsot(orderRow, options = {}) {
  const row = orderRow && typeof orderRow === "object" ? orderRow : {};
  if (isFlightsOnlyOrder(row)) {
    return {
      hotelName: "",
      source: "flight_only_order",
      rawCandidates: [],
      resolved: false,
      quoteOfferFields: null,
      quoteOfferSource: "",
    };
  }
  const or = safeOrderResponseObject(row);
  const rawCandidates = [];

  // 1. order.hotel_name / hotelName / hotelname
  pushCandidate(rawCandidates, "row.hotel_name", row.hotel_name);
  pushCandidate(rawCandidates, "row.hotelName", row.hotelName);
  pushCandidate(rawCandidates, "row.hotelname", row.hotelname);
  pushCandidate(rawCandidates, "row.HotelName", row.HotelName);

  // row.hotel object
  pushHotelDictCandidate(rawCandidates, "row.hotel", row.hotel);

  // 2. snapshot.hotel.*
  const snapshotHotels = collectDealSnapshotHotels(row, or);
  snapshotHotels.forEach((h, i) => {
    pushHotelDictCandidate(rawCandidates, `snapshot.hotel[${i}]`, h);
  });
  for (const snapKey of ["deal_snapshot", "dealSnapshot"]) {
    const snap = row[snapKey] ?? or?.[snapKey];
    if (nonEmptyObject(snap)) {
      pushCandidate(rawCandidates, `${snapKey}.hotelName`, snap.hotelName);
      pushCandidate(rawCandidates, `${snapKey}.hotel_name`, snap.hotel_name);
    }
  }

  // 3. order_response.reservation.hotel.*
  const reservation = or?.reservation;
  const resHotel = reservation?.hotel;
  pushHotelDictCandidate(rawCandidates, "order_response.reservation.hotel", resHotel);

  if (Array.isArray(reservation?.hotels)) {
    reservation.hotels.slice(0, 5).forEach((h, i) => {
      pushHotelDictCandidate(rawCandidates, `order_response.reservation.hotels[${i}]`, h);
    });
  }
  if (Array.isArray(reservation?.accommodations)) {
    reservation.accommodations.slice(0, 5).forEach((acc, i) => {
      pushHotelDictCandidate(rawCandidates, `order_response.reservation.accommodations[${i}]`, acc);
      pushHotelDictCandidate(
        rawCandidates,
        `order_response.reservation.accommodations[${i}].hotel`,
        acc?.hotel
      );
    });
  }

  pushHotelDictCandidate(rawCandidates, "order_response.hotel", or?.hotel);

  // 4. order_response.deal.hotel.*
  const dealBlock = or?.deal ?? row.deal;
  pushCandidate(rawCandidates, "order_response.deal.hotelName", dealBlock?.hotelName);
  pushHotelDictCandidate(rawCandidates, "order_response.deal.hotel", dealBlock?.hotel);

  // 5. ourprice.search_deal_raw.fullDeal.hotel.* then ourprice.hotelName
  const op = ourpriceBlock(row);
  let quoteOfferFields = extractQuoteOfferDisplayFields(null);
  const quoteOffer = resolveCustomerQuoteOfferNumberBlock(row);
  if (quoteOffer?.payload) {
    quoteOfferFields = extractQuoteOfferDisplayFields(quoteOffer.payload);
  }
  if (op) {
    const sdr = maybeJsonObject(op.search_deal_raw) ?? op.search_deal_raw;
    if (nonEmptyObject(sdr)) {
      pushCandidate(rawCandidates, "ourprice.search_deal_raw.hotelName", sdr.hotelName);
      pushHotelDictCandidate(rawCandidates, "ourprice.search_deal_raw.hotel", sdr.hotel);
      pushHotelDictCandidate(
        rawCandidates,
        "ourprice.search_deal_raw.fullDeal.hotel",
        sdr.fullDeal?.hotel
      );
    }
    pushCandidate(rawCandidates, "ourprice.hotelName", op.hotelName);
    pushCandidate(rawCandidates, "ourprice.hotel_name", op.hotel_name);
    const spj = maybeJsonObject(op.source_payload_json);
    if (spj) {
      pushCandidate(rawCandidates, "ourprice.source_payload_json.hotelName", spj.hotelName);
      const spjSdr = maybeJsonObject(spj.search_deal_raw);
      if (spjSdr) {
        pushHotelDictCandidate(
          rawCandidates,
          "ourprice.source_payload_json.search_deal_raw.fullDeal.hotel",
          spjSdr.fullDeal?.hotel
        );
      }
    }
  }

  // 5b. extended.extendedDeal[].hrDealSnapshot (Base64 JSON — post ldsDealTokenSsot)
  const hrDealSnapshotFields = pushHrDealSnapshotCandidates(rawCandidates, row, or);
  if (hrDealSnapshotFields && !quoteOfferFields.hotelName) {
    quoteOfferFields = {
      ...quoteOfferFields,
      ...hrDealSnapshotFields,
    };
  } else if (hrDealSnapshotFields) {
    quoteOfferFields = {
      hotelName: quoteOfferFields.hotelName || hrDealSnapshotFields.hotelName,
      hotelBoard: quoteOfferFields.hotelBoard || hrDealSnapshotFields.hotelBoard,
      roomType: quoteOfferFields.roomType || hrDealSnapshotFields.roomType,
      hotelNights: quoteOfferFields.hotelNights ?? hrDealSnapshotFields.hotelNights,
      hotelCity: quoteOfferFields.hotelCity || hrDealSnapshotFields.hotelCity,
    };
  }

  // 6. extended.hotelName / extended.hotel.*
  for (let i = 0; i < extendedBlocks(row, or).length; i++) {
    const ext = extendedBlocks(row, or)[i];
    pushCandidate(rawCandidates, `extended[${i}].hotelName`, ext.hotelName);
    pushCandidate(rawCandidates, `extended[${i}].hotel_name`, ext.hotel_name);
    pushHotelDictCandidate(rawCandidates, `extended[${i}].hotel`, ext.hotel);
    pushCandidate(rawCandidates, `extended[${i}].hrFlightModule.hotelName`, ext.hrFlightModule?.hotelName);
  }

  // 7. passengersinfo[].room.hotelName
  passengersInfo(row).slice(0, 5).forEach((slot, i) => {
    const rm = slot?.room;
    if (nonEmptyObject(rm)) {
      pushCandidate(rawCandidates, `passengersinfo[${i}].room.hotelName`, rm.hotelName);
      pushCandidate(rawCandidates, `passengersinfo[${i}].room.hotelname`, rm.hotelname);
      pushCandidate(rawCandidates, `passengersinfo[${i}].room.name`, rm.name);
    }
  });

  // package / search_result fallbacks (same row family)
  pushHotelDictCandidate(rawCandidates, "row.package.hotel", row.package?.hotel);
  const srHotel =
    row.search_result?.hotel ||
    row.search_result?.deal?.hotel ||
    (Array.isArray(row.search_results) && row.search_results[0]?.hotel) ||
    or?.search_result?.hotel;
  pushHotelDictCandidate(rawCandidates, "search_result.hotel", srHotel);

  // 8. dealid decoded payload
  const decoded = tryDecodeHotelNameFromRow(row, or);
  if (decoded?.value) {
    pushCandidate(rawCandidates, decoded.source, decoded.value);
  }

  // 9. linkedflights
  linkedFlightsBlock(row, or).slice(0, 5).forEach((lf, i) => {
    pushCandidate(rawCandidates, `linkedflights[${i}].hotelName`, lf.hotelName);
    pushCandidate(rawCandidates, `linkedflights[${i}].hotel_name`, lf.hotel_name);
    pushHotelDictCandidate(rawCandidates, `linkedflights[${i}].hotel`, lf.hotel);
  });

  // 10. supplement
  supplementBlock(row).slice(0, 5).forEach((sup, i) => {
    pushCandidate(rawCandidates, `supplement[${i}].hotelName`, sup.hotelName);
    pushCandidate(rawCandidates, `supplement[${i}].hotel_name`, sup.hotel_name);
    pushCandidate(rawCandidates, `supplement[${i}].name`, sup.name);
  });

  // 11. ourprice.customerQuoteDisplay.offerNumber (Base64 JSON) — last resort in chain
  if (quoteOffer?.payload && quoteOfferFields.hotelName) {
    pushCandidate(rawCandidates, quoteOffer.source, quoteOfferFields.hotelName);
  }

  let resolvedHotelName = "";
  let source = "";
  for (const c of rawCandidates) {
    if (c.banned) continue;
    if (!isUsableHotelName(c.value)) continue;
    resolvedHotelName = c.value;
    source = c.source;
    break;
  }

  const orderId = resolveOrderId(row);
  const bookingRef = resolveBookingRef(row);
  const tracePayload = {
    orderId: orderId || null,
    bookingRef: bookingRef || null,
    resolvedHotelName: resolvedHotelName || HOTEL_NAME_UNAVAILABLE,
    source: source || "none",
    rawCandidates,
    quoteOfferFields: hasQuoteOfferDisplayFields(quoteOfferFields) ? quoteOfferFields : null,
  };

  if (options.emitTrace !== false) {
    emitHotelNameTrace(tracePayload);
  }

  return {
    hotelName: resolvedHotelName || HOTEL_NAME_UNAVAILABLE,
    source,
    rawCandidates,
    resolved: Boolean(resolvedHotelName),
    quoteOfferFields: hasQuoteOfferDisplayFields(quoteOfferFields) ? quoteOfferFields : null,
    quoteOfferSource: quoteOffer?.source || "",
  };
}

/**
 * Display-safe hotel name — never returns a banned generic product label.
 * @param {object|null|undefined} orderRow
 */
export function resolveOrderHotelNameForDisplay(orderRow) {
  const row = orderRow && typeof orderRow === "object" ? orderRow : {};
  if (isFlightsOnlyOrder(row)) {
    return {
      hotelName: "",
      source: "flight_only_order",
      rawCandidates: [],
      resolved: false,
    };
  }
  const { hotelName, source, rawCandidates, resolved } = resolveOrderHotelNameSsot(orderRow, {
    emitTrace: false,
  });
  return {
    hotelName: resolved ? hotelName : HOTEL_NAME_UNAVAILABLE,
    source,
    rawCandidates,
    resolved,
  };
}

/**
 * Pick first usable title from history item title or SSOT (filters generic labels).
 * @param {string|null|undefined} historyTitle
 * @param {object|null|undefined} orderRow
 */
export function resolvePackageOrderDisplayHotelName(historyTitle, orderRow) {
  if (isUsableHotelName(historyTitle)) {
    return String(historyTitle).trim();
  }
  const ssot = resolveOrderHotelNameSsot(orderRow, { emitTrace: false });
  return ssot.resolved ? ssot.hotelName : HOTEL_NAME_UNAVAILABLE;
}
